import { t } from '../i18n/nl.js';

/**
 * Vertaalt de foutcodes van de Edge Functions `create-client`,
 * `delete-client` en `delete-client-account` naar een begrijpelijke
 * Nederlandse melding.
 *
 * De Edge Functions geven bewust stabiele codes terug (geen zinnen), zodat de
 * teksten hier via t() lopen.
 *
 * Aanmaken en verwijderen hebben elk hun EIGEN vertaalfunctie met een eigen
 * fallbacktekst. Tot 2026-07-25 deelden ze er één, waardoor een mislukte
 * verwijderpoging de melding "Onbekende fout bij het aanmaken van de cliënt"
 * toonde — een fout uit een compleet andere context.
 */

// Codes die in beide contexten dezelfde betekenis hebben (auth/sessie).
const GEDEELDE_CODES = {
  unauthorized: 'clients.efFoutGeenAutorisatie',
  geen_autorisatie: 'clients.efFoutGeenAutorisatie',
  forbidden: 'clients.efFoutGeenToegang',
  ongeldige_token: 'clients.efFoutOngeldigeToken',
  profiel_niet_gevonden: 'clients.efFoutProfielNietGevonden',
};

const AANMAAK_CODES = {
  ...GEDEELDE_CODES,
  geen_toegang: 'clients.efFoutGeenToegang',
  verplichte_velden_ontbreken: 'clients.efFoutVerplichteVelden',
  ongeldige_rol: 'clients.efFoutOngeldigeRol',
  geen_rechten_voor_rol: 'clients.efFoutGeenRechtenVoorRol',
  coach_id_verplicht: 'clients.efFoutCoachVerplicht',
  abonnement_verplicht_voor_actief: 'clients.efFoutAbonnementVerplicht',
  email_al_in_gebruik: 'clients.efFoutEmailInGebruik',
  gebruiker_aanmaken_mislukt: 'clients.efFoutGebruikerAanmakenMislukt',
  profiel_update_mislukt: 'clients.efFoutProfielUpdateMislukt',
  coach_koppeling_mislukt: 'clients.efFoutCoachKoppelingMislukt',
  abonnementstype_niet_gevonden: 'clients.efFoutAbonnementstypeNietGevonden',
  lidmaatschap_aanmaken_mislukt: 'clients.efFoutLidmaatschapMislukt',
  aanmaken_mislukt: 'clients.efFoutAanmakenMislukt',
};

const VERWIJDER_CODES = {
  ...GEDEELDE_CODES,
  client_id_required: 'clients.efFoutVerwijderGeenId',
  staf_niet_hard_verwijderbaar: 'clients.efFoutVerwijderStaf',
  auth_verwijderen_mislukt: 'clients.efFoutAuthVerwijderenMislukt',
};

/**
 * Herkent een foreign-key-schending in een rauwe Postgres-foutstring, ongeacht
 * welke tabel of constraint het betreft. Zonder dit zou elke nieuwe RESTRICT-FK
 * naar `profiles` opnieuw als onleesbare Postgres-tekst bij de gebruiker
 * belanden.
 */
function isForeignKeyFout(code) {
  if (typeof code !== 'string') return false;
  return (
    code.includes('violates foreign key constraint') ||
    code.includes('_fkey') ||
    code.includes('23503')
  );
}

export function clientEfFoutTekst(result) {
  const code = typeof result === 'string' ? result : result?.error;
  const sleutel = AANMAAK_CODES[code];
  let tekst = sleutel ? t(sleutel) : `${t('clients.efFoutOnbekend')} (${code || '-'})`;

  // Rollback mislukt: de auth-gebruiker bestaat nog en houdt het e-mailadres
  // bezet. Dat vergt handmatig ingrijpen, dus dat moet de coach expliciet zien.
  if (typeof result === 'object' && result?.rollback_mislukt) {
    tekst += ' ' + t('clients.efFoutRollbackMislukt');
  }
  return tekst;
}

/**
 * Foutteksten voor het verwijder-/anonimiseerpad. Nooit de aanmaak-teksten
 * hergebruiken: de gebruiker was hier iets heel anders aan het doen.
 */
export function clientEfVerwijderFoutTekst(result) {
  const code = typeof result === 'string' ? result : result?.error;
  const sleutel = VERWIJDER_CODES[code];
  if (sleutel) return t(sleutel);

  // Specifieke voorcontrole van delete-client: nog gekoppeld als coach.
  if (code === 'nog_gekoppeld_als_coach') {
    const b = (typeof result === 'object' && result?.blokkades) || {};
    const delen = [];
    if (b.lessen_als_coach > 0) {
      delen.push(t('clients.efFoutVerwijderDeelLessen', { aantal: b.lessen_als_coach }));
    }
    if (b.pt_aanvragen_als_coach > 0) {
      delen.push(t('clients.efFoutVerwijderDeelAanvragen', { aantal: b.pt_aanvragen_als_coach }));
    }
    if (b.overeenkomsten_aangemaakt > 0) {
      delen.push(t('clients.efFoutVerwijderDeelOvereenkomsten', { aantal: b.overeenkomsten_aangemaakt }));
    }
    return t('clients.efFoutVerwijderNogGekoppeld', { onderdelen: delen.join(', ') });
  }

  // Generieke FK-schending: kan bij ELKE tabel optreden, dus niet gebonden aan
  // één constraintnaam.
  if (isForeignKeyFout(code)) return t('clients.efFoutVerwijderGekoppeld');

  // De anonimiseer-RPC geeft bewust volzinnen terug (bv. "Deze coach staat nog
  // ingepland bij 3 toekomstige les(sen)..."). Die zijn al leesbaar Nederlands
  // en worden ongewijzigd getoond in plaats van weggegooid achter een generieke
  // fallback.
  if (typeof code === 'string' && /[a-z] [a-z]/i.test(code) && !code.includes('_')) {
    return code;
  }

  return `${t('clients.efFoutVerwijderOnbekend')} (${code || '-'})`;
}
